import { css } from 'aphrodite';
import { useMemo } from 'react';
import { FormState } from 'react-hook-form';
import { useDebounceValue } from '../../../../../hooks/useDebounceValue';
import { numberToUSDCurrencyStringValue } from '../../../../../models/UiUtils';
import { useClientCalculateDonationFee } from '../../../../../queries/Surveys/useClientCalculateDonationFee';
import { Button } from '../../../../../web/components-v2/Button';
import { Input } from '../../../../../web/components-v2/Input';
import { Checkbox } from '../../../../../web/components/Checkbox';
import { IDonationSurvey } from '../../../../models/Api';
import { DonationFrequency } from '../../../../models/enums/DonationFrequency';
import { ErrorMessage } from '../CustomerDataCollection/ErrorMessage';
import { FrequencySelector } from '../FrequencySelector';
import { IFormValues } from '../utils';
import { styleSheet } from './styles';

/**
 * Stripe requires amounts in cents (smallest currency unit)
 * This constant converts dollar amounts to cents for API calls
 */
const CENTS_PER_DOLLAR = 100;

type Props = {
	/** The donation survey being displayed */
	survey: IDonationSurvey;
	/** Whether donors can opt to cover processing fees */
	allowCustomerToCoverServiceFees: boolean;
	/** Whether donors can enter a custom amount */
	allowCustomAmount: boolean;
	/** Minimum donation amount (if custom amounts are allowed) */
	customAmountMinimum: number;
	/** Whether to show suggested donation amount buttons */
	enableSuggestedAmounts: boolean;
	/** Array of suggested donation amounts to display as buttons */
	customAmounts: number[];
	/** Whether monthly recurring donations are allowed */
	allowMonthlyDonation: boolean;
	/** Whether donor has chosen to cover fees */
	customerCoverFees: boolean;
	/** Currently selected suggested amount */
	selectedAmount?: number;
	/** Custom amount entered by donor */
	customAmount?: number;
	/** Currently selected donation frequency */
	donationFrequency?: DonationFrequency | null;
	/** Callback when a suggested amount is selected */
	onAmountChange: (amount: number) => void;
	/** Callback when custom amount changes */
	onCustomAmountChange: (customAmount: number) => void;
	/** Callback when cover fees checkbox changes */
	onCoverFeesChange: (coverFees: boolean) => void;
	/** Callback when frequency selection changes */
	onFrequencyChange: (frequency: DonationFrequency | null) => void;
	/** Form state for validation errors */
	formState?: FormState<IFormValues>;
};

/**
 * DonationSurveyValuePicker Component
 *
 * Interactive donation amount selection interface for the public donation form.
 * Provides multiple ways for donors to specify their donation amount and frequency.
 *
 * Features:
 * - Frequency selector (one-time, monthly, yearly)
 * - Suggested amount buttons (if enabled)
 * - Custom amount input field (if enabled)
 * - Processing fee coverage option with real-time fee calculation
 * - Currency formatting (displays $ symbol, stores as numeric value)
 * - Form validation and error display
 *
 * Value Handling:
 * - Amounts are stored in dollars (e.g., 25.00)
 * - Amounts are converted to cents for API calls (× 100)
 * - Fee calculations are debounced by 500ms to reduce API calls
 *
 * @param survey - The donation survey being displayed
 * @param allowCustomerToCoverServiceFees - Whether to show fee coverage option
 * @param allowCustomAmount - Whether to show custom amount input
 * @param customAmountMinimum - Minimum donation amount
 * @param enableSuggestedAmounts - Whether to show suggested amount buttons
 * @param customAmounts - Array of suggested amounts
 * @param customerCoverFees - Whether donor is covering fees
 * @param selectedAmount - Currently selected suggested amount
 * @param customAmount - Custom amount entered by donor
 * @param donationFrequency - Currently selected frequency
 * @param onAmountChange - Callback when suggested amount is selected
 * @param onCustomAmountChange - Callback when custom amount changes
 * @param onCoverFeesChange - Callback when fee coverage changes
 * @param onFrequencyChange - Callback when frequency changes
 * @param formState - Form state for validation errors
 */
export const DonationSurveyValuePicker = ({
	survey,
	allowCustomerToCoverServiceFees,
	allowCustomAmount,
	enableSuggestedAmounts,
	customAmounts,
	customerCoverFees,
	selectedAmount,
	customAmount,
	donationFrequency,
	onAmountChange,
	onCustomAmountChange,
	onCoverFeesChange,
	onFrequencyChange,
	formState,
}: Props) => {
	const orderedCustomAmounts = useMemo(() => [...customAmounts].sort((a, b) => a - b), [customAmounts]);

	// Build the list of supported recurring frequencies based on what's enabled in the survey
	const supportedRecurringFrequencies = useMemo(() => {
		const frequencies: DonationFrequency[] = [];

		if (survey.donationOptions?.monthlyDonationSettings?.enabled) {
			frequencies.push(DonationFrequency.Monthly);
		}

		if (survey.donationOptions?.annualDonationSettings?.enabled) {
			frequencies.push(DonationFrequency.Yearly);
		}

		return frequencies;
	}, [survey.donationOptions]);

	// Convert donation amount from dollars to cents for API calls
	// Priority: custom amount > selected suggested amount > null
	const value = useMemo(() => {
		if (customAmount) {
			return customAmount * CENTS_PER_DOLLAR;
		}
		if (selectedAmount) {
			return selectedAmount * CENTS_PER_DOLLAR;
		}
		return null;
	}, [customAmount, selectedAmount]);

	// Debounce value by 500ms to avoid excessive API calls during typing
	const debouncedValue = useDebounceValue(value, 500);

	const { data: fees } = useClientCalculateDonationFee({
		surveyId: survey.id,
		value: debouncedValue,
	});

	return (
		<div className={css(styleSheet.container)}>
			{/* Frequency Selector - show if recurring donations are allowed */}
			<FrequencySelector
				value={donationFrequency}
				onChange={onFrequencyChange}
				supportedRecurringFrequencies={supportedRecurringFrequencies}
				survey={survey}
			/>

			<p className={css(styleSheet.subtitle)}>Select your donation amount</p>

			{enableSuggestedAmounts && (
				<div className={css(styleSheet.amountsContainer)}>
					{orderedCustomAmounts.map((amount, index) => (
						<Button
							variant={selectedAmount === amount ? 'primary' : 'secondary'}
							key={`custom-amount-${amount}-${index}`}
							styles={[styleSheet.amountButton]}
							type='button'
							onClick={() => {
								onAmountChange(amount);
								onCustomAmountChange(null);
							}}
						>
							$ {amount}
						</Button>
					))}
				</div>
			)}

			{allowCustomAmount && (
				<div className={css(styleSheet.customAmountContainer)}>
					<Input
						id='donation-custom-amount-input'
						placeholder={enableSuggestedAmounts ? `$ Other amount` : 'Donation amount'}
						styles={[styleSheet.amountInput]}
						value={customAmount ? `$ ${customAmount}` : ''}
						onChange={e => {
							const inputValue = e.target.value;
							const numericValue = inputValue.replace(/[^0-9.]/g, '');
							const parsedValue = parseFloat(numericValue);

							if (numericValue === '' || numericValue === '.') {
								onCustomAmountChange(null);
								onAmountChange(null);
							} else if (!isNaN(parsedValue)) {
								onCustomAmountChange(parsedValue);
								onAmountChange(null);
							}
						}}
					/>
					{formState?.errors?.amount && <ErrorMessage message={formState.errors.amount.message} />}
					{formState?.errors?.customAmount && <ErrorMessage message={formState.errors.customAmount.message} />}
				</div>
			)}

			{allowCustomerToCoverServiceFees && (
				<div>
					<div className={css(styleSheet.coverFeesContainer)}>
						<Checkbox
							id='customer-cover-fees-checkbox'
							checked={customerCoverFees}
							onChange={() => onCoverFeesChange(!customerCoverFees)}
						/>
						<label htmlFor='customer-cover-fees-checkbox' className={css(styleSheet.coverFeesLabel)}>
							I&apos;d like to cover the processing fee on my donation
						</label>
						{formState?.errors?.coverFees && <ErrorMessage message={formState.errors.coverFees.message} />}
					</div>

					{customerCoverFees ? (
						<div>
							<p className={css(styleSheet.addedFeeText)}>
								We will add {fees?.serviceFee ? numberToUSDCurrencyStringValue(fees.serviceFee / 100) : '$0.00'} to the
								final donation
							</p>
						</div>
					) : null}
				</div>
			)}
		</div>
	);
};
